import { ChangeDetectorRef, Component, CUSTOM_ELEMENTS_SCHEMA, OnInit } from '@angular/core';
import { NgFor, NgIf } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { ApiUrlService } from '../../core/api-url.service';
import { finalize } from 'rxjs';

@Component({
  selector: 'app-admin-plateaux-page',
  standalone: true,
  imports: [FormsModule, NgFor, NgIf],
  templateUrl: './admin-plateaux.page.html',
  styleUrl: './admin-plateaux.page.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class AdminPlateauxPageComponent implements OnInit {
  plateaux: Plateau[] = [];
  newPlateauName = '';
  errorMessage = '';
  successMessage = '';
  isSubmitting = false;
  selectedPlateau: Plateau | null = null;
  settingsForm: PlateauSettingsForm | null = null;
  isSettingsLoading = false;
  isSettingsSaving = false;
  editingPlateauId: number | null = null;
  editPlateauName = '';
  isUpdating = false;
  deletingPlateauId: number | null = null;

  constructor(
    private readonly http: HttpClient,
    private readonly apiUrl: ApiUrlService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.loadPlateaux();
  }

  loadPlateaux(): void {
    this.http.get<Plateau[]>(this.apiUrl.build('/api/admin/plateaux')).subscribe({
      next: response => {
        this.plateaux = response;
        this.cdr.markForCheck();
      },
      error: () => {
        this.errorMessage = 'Impossible de charger les plateaux.';
        this.cdr.markForCheck();
      }
    });
  }

  createPlateau(): void {
    if (!this.newPlateauName.trim()) {
      this.errorMessage = 'Le nom du plateau est requis.';
      return;
    }
    this.errorMessage = '';
    this.successMessage = '';
    this.isSubmitting = true;
    this.http
      .post<Plateau>(this.apiUrl.build('/api/admin/plateaux'), { name: this.newPlateauName })
      .pipe(
        finalize(() => {
          this.isSubmitting = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: () => {
          this.newPlateauName = '';
          this.successMessage = 'Plateau créé.';
          this.loadPlateaux();
          this.cdr.markForCheck();
        },
        error: () => {
          this.errorMessage = 'Erreur lors de la création.';
          this.cdr.markForCheck();
        }
      });
  }

  openSettings(plateau: Plateau): void {
    this.selectedPlateau = plateau;
    this.isSettingsLoading = true;
    this.errorMessage = '';
    this.successMessage = '';
    this.http
      .get<PlateauSettingsResponse>(this.apiUrl.build(`/api/admin/plateaux/${plateau.id}/settings`))
      .pipe(
        finalize(() => {
          this.isSettingsLoading = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: response => {
          this.settingsForm = { ...response };
          this.cdr.markForCheck();
        },
        error: () => {
          this.errorMessage = 'Impossible de charger les paramètres.';
          this.cdr.markForCheck();
        }
      });
  }

  saveSettings(): void {
    if (!this.selectedPlateau || !this.settingsForm) {
      return;
    }
    this.isSettingsSaving = true;
    this.errorMessage = '';
    this.successMessage = '';
    this.http
      .put<PlateauSettingsResponse>(
        this.apiUrl.build(`/api/admin/plateaux/${this.selectedPlateau.id}/settings`),
        this.settingsForm
      )
      .pipe(
        finalize(() => {
          this.isSettingsSaving = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: response => {
          this.settingsForm = { ...response };
          this.successMessage = 'Paramètres enregistrés.';
          this.cdr.markForCheck();
        },
        error: () => {
          this.errorMessage = 'Erreur lors de la sauvegarde des paramètres.';
          this.cdr.markForCheck();
        }
      });
  }

  startEdit(plateau: Plateau): void {
    this.editingPlateauId = plateau.id;
    this.editPlateauName = plateau.name;
    this.errorMessage = '';
    this.successMessage = '';
  }

  cancelEdit(): void {
    this.editingPlateauId = null;
    this.editPlateauName = '';
  }

  updatePlateau(): void {
    if (!this.editingPlateauId) {
      return;
    }
    if (!this.editPlateauName.trim()) {
      this.errorMessage = 'Le nom du plateau est requis.';
      return;
    }
    this.isUpdating = true;
    this.errorMessage = '';
    this.successMessage = '';
    this.http
      .put<Plateau>(this.apiUrl.build(`/api/admin/plateaux/${this.editingPlateauId}`), {
        name: this.editPlateauName
      })
      .pipe(
        finalize(() => {
          this.isUpdating = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: () => {
          this.successMessage = 'Plateau mis à jour.';
          this.editingPlateauId = null;
          this.editPlateauName = '';
          this.loadPlateaux();
          this.cdr.markForCheck();
        },
        error: () => {
          this.errorMessage = 'Erreur lors de la mise à jour.';
          this.cdr.markForCheck();
        }
      });
  }

  deletePlateau(plateau: Plateau): void {
    if (!confirm(`Supprimer le plateau ${plateau.name} ?`)) {
      return;
    }
    this.deletingPlateauId = plateau.id;
    this.errorMessage = '';
    this.successMessage = '';
    this.http
      .delete<void>(this.apiUrl.build(`/api/admin/plateaux/${plateau.id}`))
      .pipe(
        finalize(() => {
          this.deletingPlateauId = null;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: () => {
          this.successMessage = 'Plateau supprimé.';
          if (this.selectedPlateau?.id === plateau.id) {
            this.selectedPlateau = null;
            this.settingsForm = null;
          }
          this.loadPlateaux();
          this.cdr.markForCheck();
        },
        error: () => {
          this.errorMessage = 'Erreur lors de la suppression.';
          this.cdr.markForCheck();
        }
      });
  }

  updatePlateauName(value: string | number): void {
    this.newPlateauName = String(value ?? '');
  }

  updateSetting(field: keyof PlateauSettingsForm, value: string | number): void {
    if (!this.settingsForm) {
      return;
    }
    const numericValue =
      field === 'planningHorizon' || field === 'warningCooldownMinutes'
        ? Number.parseInt(String(value ?? '0'), 10)
        : Number.parseFloat(String(value ?? '0'));
    this.settingsForm = {
      ...this.settingsForm,
      [field]: Number.isNaN(numericValue) ? 0 : numericValue
    };
  }
}

interface Plateau {
  id: number;
  name: string;
}

interface PlateauSettingsResponse {
  litresParPaquet: number;
  litresParMug: number;
  planningHorizon: number;
  warningCooldownMinutes: number;
}

type PlateauSettingsForm = PlateauSettingsResponse;
